import React, {useEffect} from "react";
import {
    IonButton,
    IonCard, IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent, IonIcon, IonInput, IonItem, IonLabel,
    IonLoading,
    IonPage
} from "@ionic/react";
import {
    arrowDownOutline,
    arrowDownSharp,
    arrowUpOutline,
    arrowUpSharp, personOutline, personSharp
} from "ionicons/icons";
import {AcaHeader} from "./AcaHeader";
import {useHistory, useRouteMatch} from "react-router";
import useAuth from "../../hooks/useAuth";
import {ContactTypeBrief} from "./types";
import {useMutation} from "@apollo/client";
import {ACA_SEARCH} from "../../schemas/aca";
import {formatDatetime, formatPhone, formatStatus} from "../../utils";
import PagingButtons from "../../components/PagingButtons";

const AcaAdvancedSearch: React.FC<any> = () => {
    const history = useHistory();
    const {claims} = useAuth();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);

    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const [contacts, setContacts] = React.useState<ContactTypeBrief[] | undefined>(undefined);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [numPages, setNumPages] = React.useState<number>(1);
    const [orderColumn, setOrderColumn] = React.useState<string>('CREATED_DATETIME');
    const [orderDirection, setOrderDirection] = React.useState<string>('DESC');
    const [totalCount, setTotalCount] = React.useState<number | undefined>(undefined);

    const [quickSearch, setQuickSearch] = React.useState<string | null | undefined>(undefined);
    const [contactId, setContactId] = React.useState<string | null | undefined>(undefined);
    const [opportunityId, setOpportunityId] = React.useState<string | null | undefined>(undefined);
    const [firstName, setFirstName] = React.useState<string | null | undefined>(undefined);
    const [lastName, setLastName] = React.useState<string | null | undefined>(undefined);
    const [phone, setPhone] = React.useState<string | null | undefined>(undefined);
    const [email, setEmail] = React.useState<string | null | undefined>(undefined);

    const [acaSearchMutation] = useMutation(ACA_SEARCH);
    useEffect(() => {

        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }
    }, []);

    const loadDataQuickSearch = async (page: number, column?: string, direction?: string) => {
        setIsLoading(true);
        if (!column) {
            column = orderColumn;
        }
        if (!direction) {
            direction = orderDirection;
        }
        try {
            let searchResults;
            if (quickSearch && quickSearch.length > 0) {
                searchResults = await acaSearchMutation({variables: {data: {quickSearch: quickSearch, pageSize: 10, currentPage: page, orderColumn: column, orderDirection: direction}}});
            } else {
                searchResults = await acaSearchMutation({variables: {data: {contactId: contactId ? Number(contactId) : null, opportunityId: opportunityId ? Number(opportunityId) : null, firstName: firstName, lastName: lastName, phone: phone, email: email, pageSize: 10, currentPage: page, orderColumn: column, orderDirection: direction}}});
            }
            if (searchResults.data?.acaSearch.success) {
                setContacts(searchResults.data.acaSearch.contacts);
                setNumPages(searchResults.data.acaSearch.pagination.numOfPages);
                setTotalCount(searchResults.data.acaSearch.pagination.totalCount);
            }
        } catch (e: any) {
            console.error(e);
            alert(e.message);
        } finally {
            setIsLoading(false);
        }
    };
    const performSearch = async () => {
        if (quickSearch && quickSearch.length > 0 ||
            (opportunityId || contactId || firstName || lastName || phone || email)) {
            await loadDataQuickSearch(1, 'CREATED_DATETIME', 'DESC');
        }
    };

    const changePage = async (page: number) => {
        setCurrentPage(Number(page));
        await loadDataQuickSearch(page);
    }
    const toggleOrdering = async (column: string) => {
        if (column != orderColumn) {
            setOrderColumn(column);
            setOrderDirection('DESC');
            await loadDataQuickSearch(1, column, 'DESC');
        } else {
            if (orderDirection === 'DESC') {
                setOrderDirection('ASC');
                await loadDataQuickSearch(1, column, 'ASC');
            } else {
                setOrderDirection('DESC');
                await loadDataQuickSearch(1, column, 'DESC');
            }
        }
    };

    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Advanced Search"></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-advanced-search">
                <div style={{padding: '10px'}}>

                    <IonCard>
                        <IonCardHeader>
                            <IonCardTitle>Search Filters</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <p>Choose the fields you would like to search on below.</p>
                            <table style={{width: '100%'}}>
                                <thead>

                                </thead>
                                <tbody>
                                <tr>
                                    <td colSpan={2}>
                                        <IonItem>
                                            <IonLabel>Quick Search</IonLabel>
                                            <IonInput onIonChange={(e) => setQuickSearch(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{height: '80px', verticalAlign: 'middle'}}>
                                        OR
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <IonItem>
                                            <IonLabel>Contact ID</IonLabel>
                                            <IonInput onIonChange={(e) => setContactId(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                    <td>
                                        <IonItem>
                                            <IonLabel>Opportunity ID</IonLabel>
                                            <IonInput onIonChange={(e) => setOpportunityId(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <IonItem>
                                            <IonLabel>First Name</IonLabel>
                                            <IonInput onIonChange={(e) => setFirstName(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                    <td>
                                        <IonItem>
                                            <IonLabel>Phone #</IonLabel>
                                            <IonInput onIonChange={(e) => setPhone(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <IonItem>
                                            <IonLabel>Last Name</IonLabel>
                                            <IonInput onIonChange={(e) => setLastName(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                    <td>
                                        <IonItem>
                                            <IonLabel>Email</IonLabel>
                                            <IonInput onIonChange={(e) => setEmail(e.detail.value)}></IonInput>
                                        </IonItem>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                            <p style={{textAlign: 'right'}}>
                                <IonButton onClick={() => performSearch()}>Search</IonButton>
                            </p>
                        </IonCardContent>
                    </IonCard>

                    {contacts && contacts.length > 0 &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Search Results - ACA Contacts</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <p><b>Total Contact Count:</b> {totalCount}</p>
                                {numPages &&
                                    <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                                }

                                <table className="aca-healthcare" style={{width: '100%'}}>
                                    <thead>
                                    <tr>
                                        <th>Contact ID</th>
                                        <th onClick={() => toggleOrdering('FIRST_NAME')} style={{cursor: 'pointer'}}>First
                                            Name {orderColumn === 'FIRST_NAME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'FIRST_NAME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('LAST_NAME')} style={{cursor: 'pointer'}}>Last
                                            Name {orderColumn === 'LAST_NAME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'LAST_NAME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('STATUS')} style={{cursor: 'pointer'}}>Status {orderColumn === 'STATUS' && orderDirection === 'DESC' &&
                                            <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                        }
                                            {orderColumn === 'STATUS' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('PHONE')} style={{cursor: 'pointer'}}>Phone {orderColumn === 'PHONE' && orderDirection === 'DESC' &&
                                            <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                        }
                                            {orderColumn === 'PHONE' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('EMAIL')} style={{cursor: 'pointer'}}>Email {orderColumn === 'EMAIL' && orderDirection === 'DESC' &&
                                            <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                        }
                                            {orderColumn === 'EMAIL' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th>Address</th>
                                        <th onClick={() => toggleOrdering('CREATED_DATETIME')} style={{cursor: 'pointer'}}>Created
                                            Datetime {orderColumn === 'CREATED_DATETIME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'CREATED_DATETIME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th onClick={() => toggleOrdering('LAST_UPDATED_DATETIME')} style={{cursor: 'pointer'}}>Last
                                            Updated
                                            Datetime {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'DESC' &&
                                                <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                            }
                                            {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'ASC' &&
                                                <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                            }</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {contacts && contacts.map((contact, index) => {
                                        return (
                                            <tr key={'contact_' + index}>
                                                <td style={{textAlign: 'center'}}>{contact.contactId}</td>
                                                <td>{contact.firstName}</td>
                                                <td>{contact.lastName}</td>
                                                <td>{formatStatus(contact.status)}</td>
                                                <td>{formatPhone(contact.phone)}</td>
                                                <td>{contact.email}</td>
                                                <td>{contact.addressLine1}<br/>
                                                    {contact.addressLine2 && <>{contact.addressLine2}<br/></>}
                                                    {contact.addressCity}, {contact.addressState} {contact.addressZip}</td>
                                                <td>{formatDatetime(contact.createdDatetime)}</td>
                                                <td>{formatDatetime(contact.lastUpdatedDatetime)}</td>
                                                <td>
                                                    <a href={'/aca/view-contact/' + contact.contactId}><IonIcon md={personOutline} ios={personSharp}></IonIcon></a>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                </table>
                                {numPages &&
                                    <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                                }
                                <div style={{height: '60px'}}></div>

                            </IonCardContent>
                        </IonCard>
                    }

                </div>
            </IonContent>
        </IonPage>
    );
}

export default AcaAdvancedSearch;